import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit2, Target, Calendar, Flag, ChevronRight } from 'lucide-react';
import { Button, Card, Modal } from '../../components/UI';
import { goalsApi } from '../../api';
import './Goals.css';

const PRIORITIES = [
  { value: 'high', label: 'High', color: '#722F37' },
  { value: 'medium', label: 'Medium', color: '#C9A962' },
  { value: 'low', label: 'Low', color: '#2C4A3E' },
];

const Goals = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    progress: 0,
    target_date: '',
    priority: 'medium',
    status: 'active'
  });

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      setLoading(true);
      const response = await goalsApi.getAll();
      setGoals(response.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGoal) {
        await goalsApi.update(editingGoal.id, formData);
      } else {
        await goalsApi.create(formData);
      }
      setIsModalOpen(false);
      setEditingGoal(null);
      resetForm();
      fetchGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
    }
  };

  const handleProgressChange = async (id, newProgress) => {
    try {
      await goalsApi.updateProgress(id, newProgress);
      setGoals(goals.map(g => 
        g.id === id ? { ...g, progress: newProgress } : g
      ));
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await goalsApi.delete(id);
        fetchGoals();
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description || '',
      progress: goal.progress || 0,
      target_date: goal.target_date || '',
      priority: goal.priority || 'medium',
      status: goal.status || 'active'
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      progress: 0,
      target_date: '',
      priority: 'medium',
      status: 'active'
    });
  };

  const openNewModal = () => {
    setEditingGoal(null);
    resetForm();
    setIsModalOpen(true);
  };

  const getPriorityColor = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    return p ? p.color : '#C9A962';
  };

  const getDaysRemaining = (targetDate) => {
    if (!targetDate) return null;
    const today = new Date();
    const target = new Date(targetDate);
    const diff = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const activeGoals = goals.filter(g => g.progress < 100);
  const completedGoals = goals.filter(g => g.progress >= 100);

  return (
    <div className="goals-page">
      <div className="page-header">
        <div>
          <h1>Goals</h1>
          <p className="page-subtitle">Track your aspirations and achievements</p>
        </div>
        <Button icon={Plus} onClick={openNewModal}>
          Add Goal
        </Button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading goals...</p>
        </div>
      ) : goals.length === 0 ? (
        <div className="empty-state">
          <Target size={48} strokeWidth={1} />
          <h3>No goals yet</h3>
          <p>Set your first goal and start your journey</p>
          <Button icon={Plus} onClick={openNewModal}>
            Create Your First Goal
          </Button>
        </div>
      ) : (
        <>
          {activeGoals.length > 0 && (
            <section className="goals-section">
              <h2 className="section-title">Active Goals</h2>
              <div className="goals-list">
                {activeGoals.map((goal) => {
                  const daysRemaining = getDaysRemaining(goal.target_date);
                  return (
                    <Card key={goal.id} className="goal-card" variant="gold">
                      <div className="goal-header">
                        <div className="goal-priority" style={{ backgroundColor: getPriorityColor(goal.priority) }}>
                          <Flag size={12} />
                          <span>{goal.priority}</span>
                        </div>
                        <div className="goal-actions">
                          <button className="action-btn" onClick={(e) => { e.stopPropagation(); handleEdit(goal); }}>
                            <Edit2 size={16} />
                          </button>
                          <button className="action-btn danger" onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="goal-main" onClick={() => navigate(`/goals/${goal.id}`)}>
                        <h3 className="goal-title">{goal.title}</h3>
                        {goal.description && (
                          <p className="goal-description">{goal.description}</p>
                        )}

                        <div className="goal-progress-section">
                          <div className="progress-header">
                            <span className="progress-label">Progress</span>
                            <span className="progress-value">{goal.progress}%</span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${goal.progress}%` }}
                            />
                          </div>
                        </div>

                        <div className="goal-footer">
                          {goal.target_date && (
                            <div className="goal-meta">
                              <Calendar size={14} />
                              <span>
                                {daysRemaining > 0 
                                  ? `${daysRemaining} days remaining`
                                  : daysRemaining === 0 
                                    ? 'Due today'
                                    : `${Math.abs(daysRemaining)} days overdue`
                                }
                              </span>
                            </div>
                          )}
                          <div className="view-tasks">
                            <span>View Tasks</span>
                            <ChevronRight size={16} />
                          </div>
                        </div>
                      </div>

                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={goal.progress}
                        onChange={(e) => handleProgressChange(goal.id, parseInt(e.target.value))}
                        onClick={(e) => e.stopPropagation()}
                        className="progress-slider"
                      />
                    </Card>
                  );
                })}
              </div>
            </section>
          )}

          {completedGoals.length > 0 && (
            <section className="goals-section">
              <h2 className="section-title">Completed Goals</h2>
              <div className="goals-list completed">
                {completedGoals.map((goal) => (
                  <Card key={goal.id} className="goal-card completed">
                    <div className="goal-header">
                      <div className="goal-completed-badge">
                        <Target size={12} />
                        <span>Completed</span>
                      </div>
                      <button className="action-btn danger" onClick={() => handleDelete(goal.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <h3 className="goal-title">{goal.title}</h3>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingGoal ? 'Edit Goal' : 'Create New Goal'}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Goal Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="What do you want to achieve?"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe your goal in detail..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                className="form-select"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                {PRIORITIES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Target Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
              />
            </div>
          </div>

          {editingGoal && (
            <div className="form-group">
              <label className="form-label">Progress: {formData.progress}%</label>
              <input
                type="range"
                min="0"
                max="100"
                value={formData.progress}
                onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })}
                className="form-range"
              />
            </div>
          )}

          <div className="form-actions">
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="gold">
              {editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Goals;
